import { Control, FieldValues, FormState } from 'react-hook-form';
import { Paper, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TextInput from '../../forms/TextInput';
import { ApplicationTitle } from '../../../data/models/application-title.model';
import { UserCategoryType } from '../../../data/models/user-category-type.model';
import SelectMenu from '../../forms/SelectMenu';
import Checkbox from '../../forms/Checkbox';

const PREFIX = 'GroupForm';

export const classes = {
  container: `${PREFIX}-container`,
};

export const StyledBox = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
}));

interface GroupFormProps {
  applications: ApplicationTitle[];
  categoryTypes: UserCategoryType[];
  control: Control<FieldValues, object>;
  formState: FormState<any>;
  requiredFieldMessage: string;
  isNewGroup: boolean;
  readOnly: boolean;
}

export default function GroupForm({
  applications,
  categoryTypes,
  control,
  formState,
  requiredFieldMessage,
  isNewGroup,
  readOnly,
}: GroupFormProps) {
  const { t } = useTranslation('common');

  return (
    <StyledBox className={classes.container}>
      <TextInput
        label={t('groups.groupName')}
        name="UserGroupName"
        required={true}
        disabled={readOnly}
        control={control}
        errors={formState.errors}
        inputProps={{ maxLength: 30 }}
        rules={{
          required: requiredFieldMessage,
          pattern: {
            value: /^[a-zA-Z0-9 ]*$/,
            message: t('common.alphaNumericNoPeriod'),
          },
        }}
      />
      <TextInput
        label={t('groups.description')}
        name="Description"
        required={true}
        disabled={readOnly}
        control={control}
        errors={formState.errors}
        inputProps={{ maxLength: 80 }}
        rules={{
          required: requiredFieldMessage,
          pattern: {
            value: /^[a-zA-Z0-9., ]*$/,
            message: t('common.alphaNumeric'),
          },
        }}
      />
      <SelectMenu
        label={t('groups.application')}
        name="ApplicationID"
        values={applications}
        valueAttr="applicationTitleID"
        labelAttr="applicationName"
        required={true}
        disabled={!isNewGroup}
        control={control}
        rules={{ required: requiredFieldMessage }}
      />
      <SelectMenu
        label={t('groups.userCategory')}
        name="CategoryTypeID"
        values={categoryTypes}
        valueAttr="userCategoryTypeID"
        labelAttr="userCategoryTypeName"
        required={true}
        disabled={!isNewGroup}
        control={control}
        rules={{ required: requiredFieldMessage }}
      />
      <Checkbox
        label={t('common.active')}
        name="IsActive"
        control={control}
        disabled={readOnly}
      />
    </StyledBox>
  );
}
